import * as React from 'react';
import { PiggyBank } from 'lucide-react';

import { Button } from 'components/ui/button';
import { cardVariants } from 'components/ui/card';
import ResponsiveTooltip from 'components/ui/responsive-tooltip';
import { cn } from 'lib/utils';

/**
 * Puerto real de `ExternalPlanCTAV2.tsx` (chirigota, `src/components/MiBeneficioV2/`,
 * en `IndividualDashboardV2.tsx` justo debajo de las métricas del plan) — invita a
 * declarar un plan de pensiones individual EXTERNO (otra gestora), para que cuente
 * en los límites de aportación y el ahorro fiscal.
 *
 * DOS ESTADOS, según si ya hay un plan externo declarado:
 * - Sin declarar: card oscura "hero" tipo spotlight — la luz entra desde la esquina
 *   inferior derecha a ~80º (`bg-spotlight-emerald`, verde más claro del sistema
 *   fundiéndose a oscuro), con una retícula MUY sutil superpuesta en esa misma
 *   esquina que se degrada (`grid-fade-corner`) hasta desaparecer antes de llegar
 *   al texto. Horizontal (texto a la izquierda, CTA a la derecha), sin
 *   ilustraciones literales (nada de monedas animadas — eso leía más a app cripto
 *   que a fintech serio, descartado en una iteración anterior). El titular en
 *   blanco liso (se probaron highlights de color — verde vivo, green-100 — y
 *   se volvió a blanco); el CTA usa `variant="secondary"` (contorneado, ver
 *   `ui/button.tsx`) sin flecha — no un verde hardcodeado, ya es un primitivo
 *   real.
 * - Ya declarado: resumen simple, card clara con el importe y un botón "Editar".
 *
 * REGLA DE NEGOCIO REAL — el CTA ("Sácale partido") ya NO abre el registro
 * directo: en chirigota abre un paso previo de 3 caminos (comparar con el
 * mercado / registrar para los límites / aportar más al plan ya abierto) —
 * `ExternalPlanChoiceSheet`, fuera del alcance de este puerto (aquí solo se
 * expone el `onDiscoverClick`).
 */
export interface ExternalPlanCTAProps {
	hasExternalPlan?: boolean;
	externalPlanAmount?: number;
	title?: string;
	declaredLabel?: string;
	thisYearLabel?: string;
	tooltipContent?: string;
	editLabel?: string;
	ctaTitle?: string;
	ctaDescription?: string;
	ctaButtonLabel?: string;
	onConfigureClick?: () => void;
	onDiscoverClick?: () => void;
}

export const ExternalPlanCTA = ({
	hasExternalPlan,
	externalPlanAmount,
	title = 'Plan individual externo',
	declaredLabel = 'Declarado',
	thisYearLabel = 'este año',
	tooltipContent = 'Mantén este dato al día para que calculemos bien tus límites de aportación al plan.',
	editLabel = 'Editar',
	ctaTitle = '¿Tienes otro plan de pensiones?',
	ctaDescription = 'Traspásalo a Arca o infórmate de cómo afecta a tus límites de aportación.',
	ctaButtonLabel = 'Sácale partido',
	onConfigureClick,
	onDiscoverClick
}: ExternalPlanCTAProps) => {
	if (hasExternalPlan && externalPlanAmount !== undefined && externalPlanAmount > 0) {
		return (
			// cardVariants({variant:'elevated'}), no bg-white/border-gray-200/shadow-sm a mano:
			// bug real de axe en oscuro, encontrado arreglando el contraste de abajo — esta
			// card nunca tenía tokens de tema, así que en oscuro seguía blanca de verdad
			// mientras --primary-text (pensado para fondo oscuro) se aclaraba, dejando un verde
			// claro sobre blanco (2,38:1). Mismo arreglo que ya se hizo hoy en el resto del
			// puerto (card-action.tsx, plan-card.tsx...).
			<div className={cn(cardVariants({ variant: 'elevated' }), '@container p-5')}>
				{/* Móvil: título con «i» y dato declarado, y «Editar» debajo a ancho completo. El icono solo
				    aparece si el contenido de la card mide ≥15,5rem (~290px de card): por debajo, título
				    y dato pasarían de una a dos líneas cada uno. Container query sobre la card y no
				    breakpoint de viewport, porque manda el ancho de la card, no el del móvil. Desde
				    `sm`, «Editar» pasa a la derecha. */}
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
					<div className="flex flex-1 min-w-0 items-start gap-3 sm:gap-4">
						<div className="hidden @[15.5rem]:flex w-10 h-10 rounded-lg bg-secondary/10 items-center justify-center shrink-0">
							<PiggyBank className="w-5 h-5 text-secondary" />
						</div>
						<div className="flex-1 min-w-0">
							{/* En línea, no flex: si el título envuelve, un flex deja la «i» pegada al
							    borde derecho de la caja, lejos de la última palabra. */}
							<div className="mb-1">
								<h3 className="inline text-sm font-semibold text-foreground">{title}</h3>
								<span className="ml-1.5 inline-flex align-middle">
									<ResponsiveTooltip content={tooltipContent} />
								</span>
							</div>
							<p className="text-sm text-muted-foreground">
								{declaredLabel}{' '}
								{/* text-primary-text, no text-secondary: bug real de axe (color-contrast,
								    2,2:1 en claro) — --secondary es rosa/verde según tema en este sistema,
								    no un color pensado para texto (mismo motivo que ui/Badge evita
								    --secondary para gris, ver transfer-status-card.tsx). --primary-text es
								    el token ya verificado que el resto del registry usa para cifras. */}
								<span className="font-semibold text-primary-text">
									{/* `useGrouping: true` agrupa también las cifras de 4 dígitos («1.200€»), que es-ES
									    deja sin punto por defecto; `'always'` no existe en los tipos de ES2020 que
									    compilan los proyectos que instalan esto. */}
									{externalPlanAmount.toLocaleString('es-ES', { useGrouping: true })}€
								</span>{' '}
								{thisYearLabel}
							</p>
						</div>
					</div>
					{/* Contorno solo en móvil: a ancho completo un ghost sin borde no se lee como botón. */}
					<Button
						variant="ghost"
						size="sm"
						onClick={onConfigureClick}
						className="text-primary-text hover:text-primary-text shrink-0 max-sm:w-full max-sm:border max-sm:border-input"
					>
						{editLabel}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="relative overflow-hidden rounded-2xl bg-spotlight-emerald p-5 sm:p-6 shadow-[var(--shadow-elevated)]">
			<div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-fade-corner" />
			<div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
				<div className="flex-1 min-w-0 space-y-1.5">
					<h3 className="text-lg sm:text-xl font-bold font-heading text-white leading-tight text-balance">
						{ctaTitle}
					</h3>
					{/* Medium (no bold): más peso que el "texto normal" (Inter 400) de
					    Foundations/Typography, sin subir el tamaño — sigue en Inter, el
					    body real de la app. Nunca `whitespace-nowrap`: si no cabe,
					    salta de línea (regla de Foundations/Responsive). */}
					<p className="text-base font-medium text-white/80 leading-relaxed">{ctaDescription}</p>
				</div>
				<Button
					variant="secondary"
					onClick={onDiscoverClick}
					className="text-foreground font-heading font-semibold px-5 shrink-0 w-full sm:w-auto"
				>
					{ctaButtonLabel}
				</Button>
			</div>
		</div>
	);
};
